<template>
    <v-dialog :model-value="visible"
              :persistent="true"
    >
        <v-card :rounded="true"
                :border="true"
                :flat="true"
        >
            <!-- Character Selection Phase -->
            <template v-if="phase === 'select'">
                <v-card-title class="d-flex align-center">
                    <v-icon icon="mdi-sword-cross" color="primary" class="mr-2" />
                    Guild Challenge
                </v-card-title>
                <v-card-subtitle>{{ invite.inviter_username }} has challenged you to a bout!</v-card-subtitle>
                <v-divider class="mt-3" />

                <!-- Opponent's Character -->
                <v-card-text class="pb-0">
                    <div class="text-caption text-medium-emphasis mb-2">{{ invite.inviter_username }}'s character</div>
                    <div v-if="inviterCharacter" class="carousel-item-wrapper">
                        <character-detail header-color="surface-nested" :character="inviterCharacter" />
                    </div>
                </v-card-text>

                <v-divider class="mt-4 mb-2" />

                <!-- Your Character Selection -->
                <v-card-text class="pt-0">
                    <div class="text-caption text-medium-emphasis mb-2">Choose your character</div>
                    <v-window v-model="form.character_id"
                              :show-arrows="ownedCharacters.length > 1"
                              :continuous="true"
                    >
                        <v-window-item v-for="c in ownedCharacters"
                                       :key="c.id"
                                       :value="c.id"
                        >
                            <div class="carousel-item-wrapper">
                                <character-detail header-color="surface-nested" :character="c" />
                            </div>
                        </v-window-item>
                    </v-window>
                </v-card-text>

                <v-card-text class="pt-0">
                    <div class="text-caption text-medium-emphasis text-center">
                        {{ timeLeft }}s to respond
                    </div>
                </v-card-text>

                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn @click="onDecline"
                           color="dark"
                           variant="tonal"
                           prepend-icon="mdi-cancel"
                           :disabled="submitting"
                    >
                        Decline
                    </v-btn>
                    <v-btn color="success"
                           variant="tonal"
                           @click="onAccept"
                           prepend-icon="mdi-sword-cross"
                           :loading="submitting"
                    >
                        Accept
                    </v-btn>
                </v-card-actions>
            </template>

            <!-- Connecting Phase -->
            <template v-if="phase === 'connecting'">
                <v-card-title class="d-flex align-center">
                    <v-icon icon="mdi-sword-cross" color="primary" class="mr-2" />
                    Challenge Accepted
                </v-card-title>
                <v-divider class="mt-3" />
                <v-card-text class="text-center py-6">
                    <v-progress-circular indeterminate
                                         color="primary"
                                         size="48"
                                         class="mb-4"
                    />
                    <div class="text-body-2 text-medium-emphasis">
                        Setting up bout...
                    </div>
                </v-card-text>
            </template>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import {ref, computed, onMounted, onBeforeUnmount} from "vue";
    import {storeToRefs} from "pinia";
    import {useMainStore, useGameStore} from "@/store";
    import {useRouter} from "vue-router";
    import CharacterDetail from "@/components/CharacterDetail";
    import EventBus from "@/services/eventBus";
    import WebTransportProvider from "@/services/game-manager/webTransportProvider";
    const router = useRouter();
    const mainStore = useMainStore();
    const gameStore = useGameStore();
    const {charactersDetailed: characters, references} = storeToRefs(mainStore);
    const {multiplayer, mode} = storeToRefs(gameStore);

    // A player already fighting a single-player bout can't take on a guild
    // challenge, so any incoming invite is auto-declined rather than shown. The
    // active bout is the combat screen (the `bout` route) while in single-player
    // mode.
    const inActiveSinglePlayerBout = computed(() => {
        return mode.value === 'single-player' && router.currentRoute.value.name === 'bout';
    });

    const visible = ref(false);
    const phase = ref('select');
    const submitting = ref(false);
    const timeLeft = ref(60);
    const timer = ref(null);
    const invite = ref({
        inviter_id: null,
        inviter_username: '',
        character_id: null,
        character_tier: null,
    });

    const form = ref({
        character_id: null,
    });

    const designCharacters = computed(() => {
        return references.value.data?.design?.character || [];
    });

    const inviterCharacter = computed(() => {
        const character = designCharacters.value.find(c => c.id === invite.value.character_id);
        if (! character) {
            return null;
        }
        return Object.assign({}, character, {tier: invite.value.character_tier});
    });

    const ownedCharacters = computed(() => {
        return characters.value.map(c => c)
            .sort((a, b) => {
                if (a.active && ! b.active) {
                    return -1;
                } else if (! a.active && b.active) {
                    return 1;
                }
                return a.name > b.name ? 1 : -1;
            });
    });

    function onInviteReceived(data) {
        // Decline automatically while mid-bout in single-player so the challenger
        // gets an immediate response instead of waiting for the invite to expire.
        if (inActiveSinglePlayerBout.value) {
            mainStore.declineMatchInviteRequest(data.inviter_id).catch(() => {});
            return;
        }
        invite.value = {
            inviter_id: data.inviter_id,
            inviter_username: data.inviter_username,
            character_id: data.character_id,
            character_tier: data.character_tier,
        };
        form.value.character_id = characters.value.find(c => c.active)?.id || characters.value[0]?.id;
        phase.value = 'select';
        visible.value = true;
        startTimer();
    }

    function startTimer() {
        timeLeft.value = 60;
        timer.value = setInterval(() => {
            timeLeft.value--;
            if (timeLeft.value <= 0) {
                clearInterval(timer.value);
                onDecline();
            }
        }, 1000);
    }

    function onAccept() {
        submitting.value = true;
        clearInterval(timer.value);
        mainStore.acceptMatchInviteRequest(invite.value.inviter_id, form.value.character_id)
            .then(() => {
                submitting.value = false;
                phase.value = 'connecting';
                gameStore.SET_MODE('multi-player');
                gameStore.SET_SOURCE('guild-challenge');
                gameStore.SET_CHARACTER(form.value.character_id);
                EventBus.on('multiplayer-bout', onMultiplayerBout);
            })
            .catch(() => {
                submitting.value = false;
                cleanup();
            });
    }

    function onMultiplayerBout(data) {
        EventBus.off('multiplayer-bout', onMultiplayerBout);
        gameStore.SET_MULTIPLAYER(data);
        WebTransportProvider.connect(
            multiplayer.value.hostname,
            multiplayer.value.port,
            multiplayer.value.token,
            mainStore.session
        )
            .then(() => {
                visible.value = false;
                router.push({name: 'locker-room'});
            })
            .catch(() => {
                console.log('FAILED TO CONNECT TO GAME TRANSPORT');
                visible.value = false;
            });
    }

    function onDecline() {
        if (invite.value.inviter_id) {
            mainStore.declineMatchInviteRequest(invite.value.inviter_id)
                .finally(() => {
                    cleanup();
                });
        } else {
            cleanup();
        }
    }

    function cleanup() {
        clearInterval(timer.value);
        visible.value = false;
        EventBus.off('multiplayer-bout', onMultiplayerBout);
    }

    onMounted(() => {
        EventBus.on('match-invite-received', onInviteReceived);
    });

    onBeforeUnmount(() => {
        EventBus.off('match-invite-received', onInviteReceived);
        EventBus.off('multiplayer-bout', onMultiplayerBout);
        clearInterval(timer.value);
    });
</script>

<style scoped>
    :deep(.v-window__controls .v-btn) {
        width: 22px;
        height: 22px;
        min-width: 22px;
        background-color: rgba(var(--v-theme-surface-variant), 1);
        border: none;
        color: rgba(var(--v-border-color));
        opacity: 0.8;
    }

    :deep(.v-window__controls .v-btn .v-icon) {
        font-size: 14px;
    }

    .carousel-item-wrapper {
        position: relative;
    }
</style>
